/* eslint-disable camelcase */
import React, { useState } from "react"
import { useQuery, useMutation } from "@tanstack/react-query"
import Header from "../../layouts/Header"
import BackIcon from "../../components/BackIcon"
import {
  Box,
  Paper,
  Typography,
  Divider,
  Button,
  Autocomplete,
  TextField,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material"
import { DataGrid } from "@mui/x-data-grid"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import Swal from "sweetalert2"
import CustomBackdrop from "../../components/CustomBackdrop"
import { api, showWarning } from "../../api"

// Íconos
import UploadFileIcon from "@mui/icons-material/UploadFile"
import PlayArrowIcon from "@mui/icons-material/PlayArrow"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import CancelIcon from "@mui/icons-material/Cancel"
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted"
import VisibilityIcon from "@mui/icons-material/Visibility"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" }, error: { main: "#d32f2f" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1400px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

// Función utilitaria infalible para extraer arreglos anidados sin importar cuántos wrappers existan
const extractDeep = (obj, key) => {
  if (!obj || typeof obj !== "object") return undefined
  if (key in obj) return obj[key]
  for (const k of Object.keys(obj)) {
    if (typeof obj[k] === "object") {
      const res = extractDeep(obj[k], key)
      if (res !== undefined) return res
    }
  }
  return undefined
}

const RetencionMasivaIndex = () => {
  // --- ESTADOS ---
  const [selectedCaja, setSelectedCaja] = useState(null)

  const [archivoInfo, setArchivoInfo] = useState({ nombre: "", totalExtraidas: 0 })
  const [txtData, setTxtData] = useState([])

  // Resultados del proceso
  const [exitos, setExitos] = useState([])
  const [errores, setErrores] = useState([])
  const [procesoCompletado, setProcesoCompletado] = useState(false)

  // Estado para el visor del XML
  const [modalXml, setModalXml] = useState({ open: false, data: null })

  // --- QUERYS & MUTATIONS ---
  const { data: cajasData, isLoading: isLoadingCajas } = useQuery({
    queryKey: ["getCajasMasivas"],
    queryFn: async () => {
      const response = await api.get("/RetencionMasiva/getCajas")
      const rawData = response?.data?.data || response?.data || response || []
      return Array.isArray(rawData) ? rawData : Array.isArray(rawData?.data) ? rawData.data : []
    },
    refetchOnWindowFocus: false,
  })
  const listaCajas = Array.isArray(cajasData) ? cajasData : []

  const { mutateAsync: mutValidarClaves, isPending: isValidating } = useMutation({
    mutationFn: async (payload) => await api.post("/RetencionMasiva/validarClavesMasivas", payload),
  })

  const { mutateAsync: mutProcesarLote, isPending: isProcessing } = useMutation({
    mutationFn: async (payload) => await api.post("/RetencionMasiva/procesarRetencionLote", payload),
  })

  // --- CONTROLADORES ---
  const handleFileUpload = (e) => {
    const file = e.target.files[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (evt) => {
      const text = evt.target.result
      const lines = text.split(/\r?\n/)

      if (lines.length < 2) return showWarning("El archivo está vacío o no tiene el formato correcto.")

      const headers = lines[0].split("\t")
      const idxClave = headers.findIndex((h) => h.trim() === "CLAVE_ACCESO")
      const idxRuc = headers.findIndex((h) => h.trim() === "RUC_EMISOR")
      const idxRucReceptor = headers.findIndex((h) => h.trim() === "IDENTIFICACION_RECEPTOR")
      const idxRazon = headers.findIndex((h) => h.trim() === "RAZON_SOCIAL_EMISOR")
      const idxSerie = headers.findIndex((h) => h.trim() === "SERIE_COMPROBANTE")
      const idxTotal = headers.findIndex((h) => h.trim() === "IMPORTE_TOTAL")

      if (idxClave === -1) {
        return showWarning("No se encontró la columna 'CLAVE_ACCESO' en el archivo TXT del SRI.")
      }

      if (idxRucReceptor === -1) {
        return showWarning("No se encontró la columna 'IDENTIFICACION_RECEPTOR' en el archivo TXT del SRI.")
      }

      const datosExtraidos = []

      for (let i = 1; i < lines.length; i++) {
        const columns = lines[i].split("\t")
        if (columns.length > idxClave) {
          const clave = columns[idxClave].trim()

          if (clave.length === 49) {
            datosExtraidos.push({
              clave_acceso: clave,
              ruc: idxRuc !== -1 && columns[idxRuc] ? columns[idxRuc].trim() : "N/A",
              ruc_receptor: columns[idxRucReceptor] ? columns[idxRucReceptor].trim() : "N/A",
              razon_social: idxRazon !== -1 && columns[idxRazon] ? columns[idxRazon].trim() : "N/A",
              serie: idxSerie !== -1 && columns[idxSerie] ? columns[idxSerie].trim() : "N/A",
              importe_total: idxTotal !== -1 && columns[idxTotal] ? columns[idxTotal].trim() : "0.00",
            })
          }
        }
      }

      if (datosExtraidos.length === 0) {
        return showWarning("No se encontraron claves de acceso válidas (49 dígitos) en el archivo.")
      }

      setTxtData(datosExtraidos)
      setArchivoInfo({ nombre: file.name, totalExtraidas: datosExtraidos.length })

      setExitos([])
      setErrores([])
      setProcesoCompletado(false)
    }
    reader.readAsText(file)
    e.target.value = null
  }

  const handleProcesar = async () => {
    if (!selectedCaja) return showWarning("Debe seleccionar una caja recaudadora antes de continuar.")
    if (txtData.length === 0) return showWarning("Debe cargar un archivo TXT válido del SRI.")

    const clavesList = txtData.map((t) => t.clave_acceso)
    // Obtener RUC receptor del primer registro
    const rucReceptor = txtData[0]?.ruc_receptor || "N/A"

    try {
      Swal.fire({
        title: "Fase 1/2: Validando",
        text: "Filtrando retenciones ya registradas en SIAC...",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      })

      const resValidar = await mutValidarClaves({ claves: clavesList, rucReceptor })

      // Extracción Defensiva Profunda
      const pendientes = extractDeep(resValidar, "pendientes") || []

      if (pendientes.length === 0) {
        return Swal.fire(
          "Aviso",
          "Todas las retenciones incluidas en el archivo ya se encuentran registradas en el sistema.",
          "info",
        )
      }

      Swal.fire({
        title: "Fase 2/2: Procesando Lote",
        text: `Consultando API del SRI y aplicando cobros a ${pendientes.length} retenciones... Por favor, no cierre esta ventana.`,
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      })

      const resProcesar = await mutProcesarLote({
        cjacodigo: selectedCaja.cjacodigo,
        claves_validas: pendientes,
      })

      // Extracción Defensiva Profunda para mitigar la sobre-envoltura de red
      const arrExitos = extractDeep(resProcesar, "exitos") || []
      const arrErrores = extractDeep(resProcesar, "errores") || []

      const erroresEnriquecidos = arrErrores.map((err) => {
        const infoTxt = txtData.find((t) => t.clave_acceso === err.clave_acceso) || {}
        return {
          ...err,
          ruc: infoTxt.ruc || "N/A",
          razon_social: infoTxt.razon_social || "N/A",
          importe_total: infoTxt.importe_total || "0.00",
        }
      })

      setExitos(arrExitos)
      setErrores(erroresEnriquecidos)
      setProcesoCompletado(true)

      Swal.fire(
        "Proceso Finalizado",
        `Se procesaron exitosamente ${arrExitos.length} retenciones. ${arrErrores.length} fueron rechazadas.`,
        "success",
      )
    } catch (error) {
      Swal.fire(
        "Error del Sistema",
        error?.response?.data?.message ||
          "Ocurrió un error inesperado al procesar el lote. Revise la consola del servidor.",
        "error",
      )
    }
  }

  // --- CONFIGURACIÓN DATAGRIDS ---
  const colTxtData = [
    { field: "ruc", headerName: "RUC Emisor", width: 130 },
    { field: "razon_social", headerName: "Razón Social", width: 300 },
    { field: "serie", headerName: "Serie Retención", width: 150 },
    {
      field: "importe_total",
      headerName: "Importe Total ($)",
      width: 130,
      align: "right",
      renderCell: (params) => <b style={{ color: "#196C87" }}>${params.value}</b>,
    },
    { field: "clave_acceso", headerName: "Clave de Acceso (SRI)", flex: 1, minWidth: 380 },
  ]

  const colExitos = [
    {
      field: "comprobante_siac",
      headerName: "Comprobante SIAC",
      width: 160,
      renderCell: (params) => <b style={{ color: "#2E7D32" }}>{params.value}</b>,
    },
    { field: "ruc", headerName: "RUC Emisor", width: 130 },
    {
      field: "valor_retencion",
      headerName: "Total Aplicado ($)",
      width: 140,
      align: "right",
      renderCell: (params) => <b>${Number(params.value).toFixed(2)}</b>,
    },
    { field: "clave_acceso", headerName: "Clave de Acceso (SRI)", flex: 1, minWidth: 380 },
  ]

  const colErrores = [
    { field: "ruc", headerName: "RUC Emisor", width: 130 },
    { field: "razon_social", headerName: "Razón Social", width: 250 },
    {
      field: "importe_total",
      headerName: "Importe TXT ($)",
      width: 130,
      align: "right",
      renderCell: (params) => <b>${params.value}</b>,
    },
    {
      field: "motivo",
      headerName: "Motivo del Rechazo / Error",
      flex: 1,
      minWidth: 350,
      renderCell: (params) => <span style={{ color: "#d32f2f", fontWeight: "bold" }}>{params.value}</span>,
    },
    {
      field: "acciones",
      headerName: "Auditoría XML",
      width: 150,
      align: "center",
      renderCell: (params) => (
        <Button
          size="small"
          variant="outlined"
          color="primary"
          startIcon={<VisibilityIcon />}
          onClick={() => setModalXml({ open: true, data: params.row.xml_data || null })}
        >
          Ver Datos
        </Button>
      ),
    },
    { field: "clave_acceso", headerName: "Clave de Acceso (SRI)", width: 380 },
  ]

  const isLoading = isLoadingCajas || isValidating || isProcessing

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        <div style={{ display: "flex", justifyContent: "center", margin: "0 30px 20px 30px", fontSize: "25px" }}>
          <b>Aplicación Masiva de Retenciones (SRI)</b>
        </div>

        <CustomBackdrop isLoading={isLoading} />

        <Box sx={StyledRoot}>
          {/* SECCIÓN 1: CONFIGURACIÓN Y CARGA */}
          <Paper elevation={3} sx={{ p: 4, mb: 3, borderRadius: 3 }}>
            <Typography variant="h6" color="primary" fontWeight="bold" gutterBottom>
              1. Configuración del Lote
            </Typography>
            <Typography variant="body2" color="textSecondary" mb={3}>
              Seleccione la caja donde se registrarán los cobros y cargue el archivo plano (TXT) descargado desde la
              plataforma del SRI.
            </Typography>

            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={listaCajas}
                  getOptionLabel={(option) => (option?.cjacodigo ? `${option.cjacodigo} - ${option.cjadescri}` : "")}
                  value={selectedCaja}
                  isOptionEqualToValue={(option, value) => option.cjacodigo === value?.cjacodigo}
                  onChange={(event, newValue) => {
                    setSelectedCaja(newValue)
                    setExitos([])
                    setErrores([])
                    setProcesoCompletado(false)
                  }}
                  renderInput={(params) => <TextField {...params} label="Caja Recaudadora" variant="outlined" />}
                  disabled={isLoading}
                />
              </Grid>

              <Grid item xs={12} md={5}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<UploadFileIcon />}
                    size="large"
                    disabled={isLoading}
                  >
                    Seleccionar Archivo TXT
                    <input type="file" hidden accept=".txt, .tsv" onChange={handleFileUpload} />
                  </Button>

                  {archivoInfo.nombre && (
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold" noWrap maxWidth="200px">
                        {archivoInfo.nombre}
                      </Typography>
                      <Typography variant="caption" color="secondary" fontWeight="bold">
                        {archivoInfo.totalExtraidas} claves extraídas
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} md={3} display="flex" justifyContent="flex-end">
                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  startIcon={<PlayArrowIcon />}
                  onClick={handleProcesar}
                  disabled={isLoading || txtData.length === 0 || !selectedCaja}
                  sx={{ py: 1.5, fontWeight: "bold" }}
                >
                  EJECUTAR PROCESO
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* SECCIÓN 2: AUDITORÍA DE LECTURA */}
          {txtData.length > 0 && !procesoCompletado && (
            <Paper elevation={3} sx={{ p: 4, mb: 3, borderRadius: 3 }}>
              <Typography
                variant="h6"
                color="primary"
                fontWeight="bold"
                gutterBottom
                sx={{ display: "flex", alignItems: "center" }}
              >
                <FormatListBulletedIcon sx={{ mr: 1 }} /> 2. Detalle de Retenciones a Procesar (Leídas del Archivo)
              </Typography>
              <Typography variant="body2" color="textSecondary" mb={2}>
                Revise la información extraída del TXT antes de ejecutar el proceso masivo.
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ height: 400, width: "100%" }}>
                <DataGrid
                  rows={txtData}
                  columns={colTxtData}
                  getRowId={(row) => row.clave_acceso}
                  density="compact"
                  disableRowSelectionOnClick
                  sx={{ "& .MuiDataGrid-columnHeaders": { backgroundColor: "#e3f2fd", color: "#196C87" } }}
                />
              </Box>
            </Paper>
          )}

          {/* SECCIÓN 3: RESULTADOS */}
          {procesoCompletado && (
            <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
              <Box mb={4}>
                <Typography
                  variant="h6"
                  color="secondary"
                  fontWeight="bold"
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <CheckCircleIcon sx={{ mr: 1 }} /> Retenciones Aplicadas Exitosamente ({exitos.length})
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ height: 350, width: "100%" }}>
                  <DataGrid
                    rows={exitos}
                    columns={colExitos}
                    getRowId={(row) => row.clave_acceso}
                    density="compact"
                    disableRowSelectionOnClick
                    sx={{ "& .MuiDataGrid-columnHeaders": { backgroundColor: "#e8f5e9", color: "#2E7D32" } }}
                  />
                </Box>
              </Box>

              <Box>
                <Typography
                  variant="h6"
                  color="error"
                  fontWeight="bold"
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <CancelIcon sx={{ mr: 1 }} /> Log de Errores - Retenciones Rechazadas ({errores.length})
                </Typography>
                <Typography variant="body2" color="textSecondary" mb={2}>
                  Estas retenciones no fueron aplicadas en SIAC. Puede ver el detalle de los montos extraídos del SRI
                  haciendo clic en "Ver Datos".
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ height: 350, width: "100%" }}>
                  <DataGrid
                    rows={errores}
                    columns={colErrores}
                    getRowId={(row) => row.clave_acceso}
                    density="compact"
                    disableRowSelectionOnClick
                    sx={{ "& .MuiDataGrid-columnHeaders": { backgroundColor: "#ffebee", color: "#d32f2f" } }}
                  />
                </Box>
              </Box>
            </Paper>
          )}
        </Box>

        {/* MODAL VISOR DE XML */}
        <Dialog open={modalXml.open} onClose={() => setModalXml({ open: false, data: null })} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ bgcolor: "#f5f5f5", color: "primary.main", fontWeight: "bold" }}>
            Datos Extraídos del SRI (XML)
          </DialogTitle>
          <DialogContent dividers>
            {modalXml.data ? (
              <Box>
                <Typography variant="subtitle2" color="textSecondary">
                  Estructura detectada en el comprobante electrónico:
                </Typography>
                <pre
                  style={{
                    backgroundColor: "#2d2d2d",
                    color: "#a9dc76",
                    padding: "15px",
                    borderRadius: "8px",
                    overflowX: "auto",
                    fontSize: "12px",
                  }}
                >
                  {JSON.stringify(modalXml.data, null, 2)}
                </pre>
              </Box>
            ) : (
              <Typography variant="body2" color="textSecondary">
                El backend no logró extraer información estructurada o el comprobante no es válido. Revise el motivo del
                rechazo en la tabla.
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setModalXml({ open: false, data: null })} color="inherit" variant="contained">
              Cerrar Visor
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </ThemeProvider>
  )
}

export default RetencionMasivaIndex
